import { CancelOutlined, CheckCircleOutlined, Groups, LocalActivity } from '@mui/icons-material';
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import Colors from "../colors";
import ButtonOutline from "../components/ButtonOutline";
import InfoRow from "../components/InfoRow";
import LoadingSpinner from "../components/LoadingSpinner";
import SimpleRowCard from "../components/SimpleRowCard";
import ConfirmationModal from "../components/modals/ConfirmationModal";
import EditMembersModal from "../components/modals/EditMembersModal";
import EditSpotCheckModal from "../components/modals/EditSpotCheckModal";
import SingleTextInputModal from "../components/modals/SafetyTailgateModals/SingleTextInputModal";
import { createNewDocInDb, deleteDocByCollectionAndId, deleteDocsByCollectionAndFK, getAllDocsBySubCollection, getDocByCollectionAndId, getDocsByCollectionAndFK, updateDocByCollectionAndId } from "../firebase";
import { Snackbar, Alert, Divider } from '@mui/material';
import EncourageCreation from '../components/EncourageCreation';

function SafetyAudit({ user }) {

    const containerStyles = {
        textAlign: 'center',
        paddingTop: '25px',
    };

    const subtitleStyles = {
        fontSize: '23px',
        fontWeight: 500,
        display: 'block',
        marginBottom: '5px',
        marginTop: '20px'
    }

    const btnGroupStyles = {
        marginTop: '40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
    }

    const cardContainerStyles = {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        marginTop: '12px'
    }

    const { safetyAuditId } = useParams();
    const [formId, setFormId] = useState('');
    const navigate = useNavigate();
    const [spotChecks, setSpotChecks] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [isArchived, setIsArchived] = useState(true);

    const [editMemberModalIsVisible, setEditMemberModalIsVisible] = useState(false);
    const [activityModalIsVisible, setActivityModalIsVisible] = useState(false);
    const [spotCheckModalIsVisible, setSpotCheckModalIsVisible] = useState(false);
    const [deleteModalIsVisible, setDeleteModalIsVisible] = useState(false);

    const [activity, setActivity] = useState('');
    const [members, setMembers] = useState([]);
    const [measures, setMeasures] = useState([]);

    const [selectedMembers, setSelectedMembers] = useState([]);
    const [selectedMeasures, setSelectedMeasures] = useState([]);
    const [selectedTime, setSelectedTime] = useState();
    const [selectedCompliant, setSelectedCompliant] = useState(true);
    const [selectedDescription, setSelectedDescription] = useState('');
    const [selectedId, setSelectedId] = useState('');

    const [modalCreateStatus, setModalCreateStatus] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const updateActivity = (newActivity) => {
        setActivity(newActivity);
        updateDocByCollectionAndId('SafetyAudits', safetyAuditId, { activity: newActivity });
    }

    const updateMembers = (newMembers) => {
        setSelectedMembers(newMembers);
        updateDocByCollectionAndId('SafetyAudits', safetyAuditId,
            {
                formMemberIds: newMembers.map(member => member.value)
            });
    }

    const createCheck = async () => {
        const newCheck = {
            safetyAuditId: safetyAuditId,
            compliant: selectedCompliant,
            time: selectedTime,
            measures: selectedMeasures,
            description: selectedDescription,
        }
        const newDoc = await createNewDocInDb('SpotChecks', newCheck);
        setSpotChecks([newDoc, ...spotChecks]);
    };

    function editCheck() {
        spotChecks.forEach(check => {
            if (check.id === selectedId) {
                check.compliant = selectedCompliant;
                check.time = selectedTime;
                check.measures = selectedMeasures;
                check.description = selectedDescription;
            }
        });
        setSpotChecks(spotChecks);
        updateDocByCollectionAndId('SpotChecks', selectedId,
            {
                compliant: selectedCompliant,
                time: selectedTime,
                measures: selectedMeasures,
                description: selectedDescription
            });
    };

    const deleteCheck = async () => {
        setSpotChecks(spotChecks.filter(check => check.id !== selectedId));
        await deleteDocByCollectionAndId('SpotChecks', selectedId);
    }

    const deleteAudit = async () => {
        await deleteDocsByCollectionAndFK('SpotChecks', 'safetyAuditId', safetyAuditId);
        await deleteDocByCollectionAndId('SafetyAudits', safetyAuditId);
        navigate(`/safety-audits/${formId}`)
    }

    const getIcon = (compliant) => {
        if (compliant) {
            return <CheckCircleOutlined sx={{ color: Colors.green }} />
        } else {
            return <CancelOutlined sx={{ color: Colors.red }} />
        }
    }

    const saveCallback = () => {
        if (modalCreateStatus) createCheck();
        else editCheck();
    }


    const showEditCheckModal = (check) => {
        setModalCreateStatus(false);
        setSelectedTime(check.time);
        setSelectedCompliant(check.compliant);
        setSelectedDescription(check.description);
        setSelectedMeasures(check.measures);
        setSelectedId(check.id);
        setSpotCheckModalIsVisible(true);
    }

    const showAddCheckModal = () => {
        setModalCreateStatus(true);
        setSelectedTime();
        setSelectedCompliant();
        setSelectedDescription('');
        setSelectedMeasures([]);
        setSelectedId();
        setSpotCheckModalIsVisible(true);
    }

    const formatDate = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })

    const getMembersMainText = (membersList) => {
        const max = 5;
        let membersStr = membersList.map((m, i) => {
            return `${m.firstName} ${m.lastName[0]}`
        }).slice(0, max).join(', ')
        const diff = membersList.length - max;
        if (diff > 1) membersStr += `, and ${diff} others`;
        else if (diff > 0) membersStr += `, and ${diff} other`;
        return membersStr;
    }

    useEffect(() => {
        if (!user) return;
        const fetchChecks = async () => {
            const docs = await getDocsByCollectionAndFK("SpotChecks", 'safetyAuditId', safetyAuditId);
            const spotCheckList = [];
            docs.forEach(doc => {
                const newObj = {
                    id: doc.id,
                    compliant: doc.compliant,
                    time: doc.time.toDate(),
                    measures: doc.measures,
                    description: doc.description,
                }
                spotCheckList.push(newObj);
            });
            const sortedRows = spotCheckList.sort((a, b) => b.time - a.time);
            setSpotChecks(sortedRows);
        };

        const fetchAudit = async () => {
            const data = await getDocByCollectionAndId("SafetyAudits", safetyAuditId);
            let allMembers = await getAllDocsBySubCollection('Forms', data.formId, 'FormMembers');
            allMembers = allMembers.map(member => ({
                firstName: member.firstName,
                lastName: member.lastName,
                label: member.firstName + ' ' + member.lastName,
                value: member.id
            }));
            const form = await getDocByCollectionAndId('Forms', data.formId);
            const allMeasures = form.controlMeasures;
            setIsArchived(form.isArchived || form.userId !== user.uid);
            if (form.isArchived || form.userId !== user.uid) setSnackbarOpen(true);
            setMeasures(allMeasures);
            setMembers(allMembers);
            setSelectedMembers(allMembers.filter(member => data.formMemberIds.includes(member.value)));
            setActivity(data.activity);
            setFormId(data.formId);
            setLoadingStatus(false);
        };
        fetchChecks()
        fetchAudit();
    }, [user, navigate, safetyAuditId]);

    return (
        <div style={containerStyles}>
            <Snackbar open={snackbarOpen} autoHideDuration={10000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ marginTop: '50px' }}>
                <Alert severity="info" sx={{ width: '100%' }}>
                    This Audit is archived or you do not have permission to edit it.
                </Alert>
            </Snackbar>
            <InfoRow iconComponent={<Groups sx={{ fontSize: '43px' }} />} title={'members'} mainText={getMembersMainText(selectedMembers)} isEditable={!isArchived} onClickHandler={() => setEditMemberModalIsVisible(true)} />
            <InfoRow iconComponent={<LocalActivity sx={{ fontSize: '42px', color: Colors.blue }} />} title={'activity'} mainText={activity} isEditable={!isArchived} onClickHandler={() => setActivityModalIsVisible(true)} />
            <LoadingSpinner isLoading={loadingStatus} />
            <span style={subtitleStyles}>Spot Checks</span>
            <Divider />
            <div style={cardContainerStyles}>
                {spotChecks.length === 0 && !isArchived && <EncourageCreation headerText={'(0) Spot Checks found'} suggestionText={'Tap the add button to get started!'} borderColor={Colors.blue} />}
                {
                    spotChecks.map((check, index) => (
                        <SimpleRowCard
                            style={{ borderColor: Colors.blue }}
                            disabled={isArchived}
                            key={'spotCheck' + index}
                            icon={getIcon(check.compliant)}
                            label={formatDate(check.time)}
                            onClick={() => showEditCheckModal(check)}
                        />
                    ))
                }
            </div>
            {!isArchived ?
                <div style={btnGroupStyles}>
                    <ButtonOutline color={Colors.blue} onClick={showAddCheckModal}>Add Spot Check</ButtonOutline>
                    <ButtonOutline color={Colors.red} onClick={() => setDeleteModalIsVisible(true)}>Delete Audit</ButtonOutline>
                </div>
                :
                <div style={btnGroupStyles}>
                    <span>No changes can be made to this safety audit, as its Tailgate form has been archived.</span>
                </div>
            }
            <EditMembersModal title={'Edit Members'} inputLabel={'Members'} isVisible={editMemberModalIsVisible} setIsVisible={setEditMemberModalIsVisible} options={members} defaultSelected={selectedMembers} dataCallback={updateMembers} />
            <SingleTextInputModal modalLabel={'Edit Activity'} inputLabel={'Activity'} defaultValue={activity} isVisible={activityModalIsVisible} setIsVisible={setActivityModalIsVisible} dataCallback={updateActivity} />
            <EditSpotCheckModal isVisible={spotCheckModalIsVisible} setIsVisible={setSpotCheckModalIsVisible} time={selectedTime} setTime={setSelectedTime} compliant={selectedCompliant} setCompliant={setSelectedCompliant} measures={selectedMeasures} setMeasures={setSelectedMeasures} allMeasures={measures} description={selectedDescription} setDescription={setSelectedDescription} add={modalCreateStatus} saveCallback={saveCallback} deleteCallback={deleteCheck} isEditable={!isArchived} />
            <ConfirmationModal isVisible={deleteModalIsVisible} setIsVisible={setDeleteModalIsVisible} mainAction={'Delete'} mainActionHandler={deleteAudit} title={'Are you sure you want to delete this Safety Audit?'} />
        </div>
    )
}

export default SafetyAudit;